import React, { useEffect, useState } from 'react';
import { usePromptTemplateService, PromptTemplateType } from '../services/promptTemplateService';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

const AdminPromptStructures: React.FC = () => {

  const navigate = useNavigate();
  const { fetchPromptTemplates } = usePromptTemplateService();
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplateType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function handleBack(): void {
    navigate('/admin');
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchPromptTemplates()
      .then(setPromptTemplates)
      .catch((e) => setError(e.message || 'Failed to load prompt structures'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      py: 8,
      p: 4,
      height: '100vh',
      overflowY: 'auto'
    }}>
      <Box sx={{
        width: { xs: '95%', md: '95%', lg: '95%', xl: '85%' },
        p: 4,
        borderRadius: 3,
      }}>
        {/* Header Row */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          mb: 4
        }}>
          <PageHeader
            title="Prompt Structures Management"
            subtitle="View, edit, or create new prompt structures below."
            onBack={handleBack}
            showBackButton={true}
          />
          {/* Sort Dropdown aligned right and bottom */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            height: '100%'
          }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/admin/prompt-structures/create')}
              sx={{ minWidth: 180 }}
            >
              Create Prompt Structure
            </Button>
          </Box>
        </Box>
        {/* Scenario Cards Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr', xl: '1fr 1fr 1fr' },
            gap: 3,
          }}
        >
          {loading && (
            <Typography variant="body1">Loading prompt structures...</Typography>
          )}
          {error && (
            <Typography color="error" variant="body1">{error}</Typography>
          )}
          {!loading && !error && promptTemplates.length === 0 && (
            <Typography variant="body1">No prompt structures found.</Typography>
          )}
          {!loading && !error && promptTemplates.map((pt) => (
            <Card sx={{ height: '100%' }} key={pt.id}>
              <CardContent>
                <Typography variant="h6">{pt.title}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>{pt.description || 'No description.'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Created by: {pt.created_by || 'Unknown'}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => navigate(`/admin/prompt-structures/${pt.id}`)}>Edit</Button>
                <Button size="small" onClick={() => navigate(`/admin/prompt-structures/${pt.id}?copy=1`)}>Copy</Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPromptStructures;